import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Target, Eye, Award, MapPin, Users, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import teamImage from "@/assets/team-collaboration.jpg";

const About = () => {
  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description: "To transform every customer-facing process with intelligent, compliant, and scalable AI voice agents that deliver exceptional experiences.",
      gradient: "from-gradient-pink/20 to-gradient-purple/20",
    },
    {
      icon: Eye,
      title: "Our Vision",
      description: "A world where businesses can scale customer interactions infinitely while maintaining quality, compliance, and cost efficiency.",
      gradient: "from-gradient-purple/20 to-gradient-blue/20",
    },
    {
      icon: Award,
      title: "Our Values",
      description: "Innovation, compliance, transparency, and customer success. We build technology that enterprises can trust.",
      gradient: "from-gradient-blue/20 to-gradient-cyan/20",
    },
  ];

  const milestones = [
    { year: "2025", event: "Holionix GmbH founded in Cologne and onboarded 1st customer in 1st 2 months of product launch, now Global expansion & ISO 27001 certification", icon: Award },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-shadow-glow">
              About <span className="gradient-brand-text">Holionix</span>
            </h1>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              We're building the future of enterprise customer interactions with AI voice agents that are compliant, scalable, and indistinguishable from human conversations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`p-8 card-dark text-center hover:glow-effect transition-all duration-300 h-full bg-gradient-to-br ${value.gradient}`}>
                  <div className="w-14 h-14 rounded-lg gradient-brand flex items-center justify-center mx-auto mb-4 glow-effect">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-foreground/70">
                    {value.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          <Card className="p-12 card-dark gradient-border mb-20 overflow-hidden relative">
            <div className="absolute inset-0 opacity-10">
              <img src={teamImage} alt="Team collaboration" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Story</h2>
              <p className="text-lg text-foreground/80 leading-relaxed mb-4">
                Holionix GmbH was founded in Cologne, Germany with a singular focus: bringing enterprise-grade AI voice automation to customer-facing processes. We recognized that businesses needed more than just chatbots—they needed intelligent voice agents that could handle complex conversations, maintain compliance, and scale infinitely.
              </p>
              <p className="text-lg text-foreground/80 leading-relaxed">
                Today, we serve enterprises across telecom, finance, healthcare, e-commerce, and BPO sectors, helping them reduce costs by up to 40% while improving customer satisfaction and operational efficiency.
              </p>
            </div>
          </Card>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Journey</h2>
            <div className="max-w-3xl mx-auto">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="flex items-start gap-6 mb-8"
                >
                  <div className="w-16 h-16 rounded-full gradient-brand flex items-center justify-center flex-shrink-0 glow-effect">
                    <milestone.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-grow pt-3">
                    <div className="text-2xl font-bold gradient-brand-text mb-2">{milestone.year}</div>
                    <p className="text-lg text-foreground/80">{milestone.event}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center mb-20">
            <h2 className="text-3xl font-bold mb-6">Our Location</h2>
            <Card className="max-w-md mx-auto p-8 card-dark gradient-border">
              <MapPin className="w-12 h-12 text-accent mx-auto mb-4" />
              <p className="text-lg font-semibold mb-2">
                <strong>Holionix GmbH</strong>
              </p>
              <p className="text-foreground/70 mb-1">Cologne, Germany 🇩🇪</p>
              <p className="text-foreground/70">Serving enterprises globally</p>
            </Card>
          </div>

          <Card className="p-12 card-dark gradient-border text-center glow-effect">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Customer Service?</h2>
            <p className="text-lg text-foreground/70 mb-8 max-w-2xl mx-auto">
              Join leading enterprises who trust Holionix to automate and enhance their customer interactions.
            </p>
            <Link to="/contact">
              <Button size="lg" className="gradient-brand text-white hover:opacity-90 text-lg px-10 py-6">
                Get in Touch
              </Button>
            </Link>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;
